import {isString} from 'lodash'
import moment, {Moment} from 'moment-timezone'

import type {DateRange, DayTime, ShiftQuickSelectSlots, TimeZone} from '../types'

export const getUtcOffset = (timezone?: string, date?: Moment): number => {
  if (!timezone) {
    return 0
  }
  if (!date) {
    return moment().tz(timezone).utcOffset()
  }
  return date.tz(timezone).utcOffset()
}

/**
 * Returns the hours and minutes of a time string
 * @param time 'HH:mm:ss'
 * @returns 'HH:mm'
 */
export const getHoursMinutes = (time?: string) => {
  if (!time) return ''
  const [hours, minutes] = time.split(':')
  return `${hours}:${minutes}`
}

function convertDateToUtc(date: Moment, fromTimezone: string): Moment {
  const timezoned = moment.tz(date, fromTimezone)
  const offset = timezoned.utcOffset()
  return timezoned.clone().add(offset, 'minutes').utc()
}
function convertDateUtcToLocalTime(date: Moment, toTimezone: string) {
  const timezoned = moment.tz(date, toTimezone)
  const offset = timezoned.utcOffset()
  return timezoned.clone().subtract(offset, 'minutes').utc()
}
export function fromBrowsertimeToPlantTime(date: Moment, fromTimezone: string, toTimezone: string) {
  return convertDateUtcToLocalTime(convertDateToUtc(date, fromTimezone), toTimezone)
}

export const timezonedDateValidator = (a?: Moment | null, b?: Moment | null) => {
  const timezone = a?.tz()
  if (b && timezone && b.tz() !== timezone) {
    throw new Error(
      `Provided date is not in the plant timezone. Expected: ${timezone}, received: ${b.tz()}`
    )
  }
}

/**
 * Validates that a date range is in the specified plant timezone.
 *
 * @param plantTimezone - The expected timezone for the date range (e.g., 'Europe/Berlin')
 * @param dateRange - The date range to validate, or a shift quick select slot string
 * @throws {Error} When the date range's start or end dates are not in the plant timezone
 *
 * @example
 * ```typescript
 * const range = { startDate: moment.tz('2024-01-01', 'Europe/Berlin'), endDate: moment.tz('2024-01-02', 'Europe/Berlin') }
 * plantTimezonedDateRangeValidator('Europe/Berlin', range) // No error
 *
 * const invalidRange = { startDate: moment.tz('2024-01-01', 'UTC'), endDate: moment.tz('2024-01-02', 'Europe/Berlin') }
 * plantTimezonedDateRangeValidator('Europe/Berlin', invalidRange) // Throws Error
 * ```
 */
export const plantTimezonedDateRangeValidator = (
  plantTimezone: string,
  dateRange?: DateRange | ShiftQuickSelectSlots
) => {
  if (
    dateRange &&
    !isString(dateRange) &&
    (dateRange.startDate.tz() !== plantTimezone || dateRange.endDate.tz() !== plantTimezone)
  ) {
    throw new Error(
      `Provided date range is not in the plant timezone. Expected: ${plantTimezone}, received: ${dateRange.startDate.tz()} - ${dateRange.endDate.tz()}`
    )
  }
}

export function getHours(timeString: DayTime) {
  return Number.parseInt(timeString.split(':')[0]) || 0
}

export function getMinutes(timeString: DayTime) {
  return Number.parseInt(timeString.split(':')[1]) || 0
}

export function getHoursAndMinutesFromTimeString(timeString: DayTime) {
  return [getHours(timeString), getMinutes(timeString)]
}

export const URL_DATE_FORMAT = 'YYYYMMDDHHmmssSSS'

export const getSystemTimezone = (): TimeZone => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Formats a Moment date object to a localized string with date and time.
 * Uses moment's L (localized date) and LT (localized time) format tokens.
 *
 * @param date - The Moment date object to format
 * @param separator - The separator between date and time (default: '-')
 * @returns Formatted date string or '-' if date is undefined
 *
 * @example
 * // With English locale: "01/15/2026 - 2:30 PM"
 * // With German locale: "15.01.2026 - 14:30"
 */
export const formatDateTime = (date?: Moment, separator = '-'): string =>
  date ? date.format(`L ${separator} LT`) : '-'
